import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, X, Maximize2 } from "lucide-react";
import { resolveImage } from "@/lib/api";

export function PhotoGallery({ images = [], title = "" }) {
  const [emblaRef, embla] = useEmblaCarousel({ loop: true, align: "center" });
  const [index, setIndex] = useState(0);
  const [lightbox, setLightbox] = useState(null);

  const onSelect = useCallback(() => { if (embla) setIndex(embla.selectedScrollSnap()); }, [embla]);
  useEffect(() => {
    if (!embla) return undefined;
    embla.on("select", onSelect);
    onSelect();
    return () => embla.off("select", onSelect);
  }, [embla, onSelect]);

  useEffect(() => {
    if (lightbox === null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight") setLightbox((i) => (i + 1) % images.length);
      if (e.key === "ArrowLeft") setLightbox((i) => (i - 1 + images.length) % images.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, images.length]);

  if (!images.length) return null;

  return (
    <section className="mx-auto mt-10 max-w-5xl px-5 sm:px-8" data-testid="photo-gallery">
      <div className="flex items-end justify-between">
        <div>
          <p className="eyebrow text-[var(--saffron-deep)]">तस्वीरें · Gallery</p>
          <h2 className="mt-1 font-serif text-2xl font-bold text-[var(--chakra-deep)]">Photos</h2>
        </div>
        <span className="text-sm text-stone-500" data-testid="gallery-counter">{index + 1} / {images.length}</span>
      </div>

      <div className="relative mt-5">
        <div className="overflow-hidden rounded-3xl border border-[var(--line)] bg-[var(--saffron-tint)]" ref={emblaRef}>
          <div className="flex">
            {images.map((src, i) => (
              <div key={src + i} className="relative min-w-0 flex-[0_0_100%]" data-testid={`gallery-slide-${i}`}>
                <img src={resolveImage(src)} alt={`${title} photo ${i + 1}`} className="mx-auto max-h-[70vh] w-full object-contain" loading={i === 0 ? "eager" : "lazy"} />
                <button onClick={() => setLightbox(i)} aria-label="View full size" data-testid={`gallery-expand-${i}`} className="absolute right-3 top-3 rounded-full bg-white/90 p-2 text-stone-700 shadow transition-colors hover:text-[var(--chakra)]">
                  <Maximize2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
        {images.length > 1 && (
          <>
            <button onClick={() => embla?.scrollPrev()} aria-label="Previous photo" data-testid="gallery-prev-btn" className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-2 text-[var(--chakra-deep)] shadow-lg transition-colors hover:bg-[var(--saffron)] hover:text-white"><ChevronLeft className="h-5 w-5" /></button>
            <button onClick={() => embla?.scrollNext()} aria-label="Next photo" data-testid="gallery-next-btn" className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-2 text-[var(--chakra-deep)] shadow-lg transition-colors hover:bg-[var(--saffron)] hover:text-white"><ChevronRight className="h-5 w-5" /></button>
          </>
        )}
      </div>

      {images.length > 1 && (
        <div className="mt-4 flex gap-2 overflow-x-auto pb-1" data-testid="gallery-thumbs">
          {images.map((src, i) => (
            <button key={src + i} onClick={() => embla?.scrollTo(i)} aria-label={`Go to photo ${i + 1}`} data-testid={`gallery-thumb-${i}`}
              className={`h-16 w-24 shrink-0 overflow-hidden rounded-lg border-2 transition-all ${i === index ? "border-[var(--saffron)] opacity-100" : "border-transparent opacity-60 hover:opacity-100"}`}>
              <img src={resolveImage(src)} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}

      {lightbox !== null && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-4" onClick={() => setLightbox(null)} data-testid="gallery-lightbox">
          <img src={resolveImage(images[lightbox])} alt="" className="max-h-full max-w-full object-contain" onClick={(e) => e.stopPropagation()} />
          <button className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20" aria-label="Close" data-testid="gallery-lightbox-close"><X className="h-6 w-6" /></button>
          {images.length > 1 && (
            <>
              <button onClick={(e) => { e.stopPropagation(); setLightbox((lightbox - 1 + images.length) % images.length); }} className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-3 text-white hover:bg-white/20" aria-label="Previous"><ChevronLeft className="h-6 w-6" /></button>
              <button onClick={(e) => { e.stopPropagation(); setLightbox((lightbox + 1) % images.length); }} className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-3 text-white hover:bg-white/20" aria-label="Next"><ChevronRight className="h-6 w-6" /></button>
            </>
          )}
          <span className="absolute bottom-4 left-1/2 -translate-x-1/2 text-sm text-white/80">{lightbox + 1} / {images.length}</span>
        </div>
      )}
    </section>
  );
}
